<div class="flex flex-col items-center justify-center w-full pt-16">
  <h2 class="pb-12 text-[28px] font-medium">
    {{ 'pages.profile.banner.title' | translate }}
  </h2>

  <app-content-container class="min-w-[524px]">
    <div class="flex flex-col gap-4">
      <section class="flex flex-col gap-6">
        <!-- Add user image field -->
        @if (!(authService.currentDbUser$ | async)?.creatorId) {
          <div *hasPermission="EPermissions.userUpdateOwn" class="flex justify-center items-center">
            <form [formGroup]="imageForm" class="custom-form">
              <app-file-uploader
                class="w-full"
                shape="circle"
                [imageWidth]="124"
                [imageHeight]="124"
                [imageId]="imageForm.controls.iconId.value"
                [fileForm]="imageForm.controls.imageFile"
                [requiredPermission]="[EPermissions.userUpdateOwn]"
                [showEditButton]="true"
                (imageSelected)="onImageSelected($event)"
                (imageRemoved)="onImageRemoved()"
                #fileUploaderComponent
              />
            </form>
          </div>
        }

        <!-- user name field -->
        <form [formGroup]="profileForm" class="flex flex-col gap-6 custom-form">
          @if (!(authService.currentDbUser$ | async)?.creatorId) {
            <mat-form-field
              class="flex-1 custom-form [&_input]:pointer-events-none"
              *hasPermission="EPermissions.userUpdateOwn"
            >
              <mat-label>{{ 'pages.profile.form.name' | translate }}</mat-label>
              <input matInput formControlName="name" />
              <mat-error [control]="profileForm.get('name')" />
            </mat-form-field>
          }

          <!--  user language field -->
          <mat-form-field class="flex-1 custom-form [&_input]:pointer-events-none">
            <mat-label>{{ 'pages.profile.language.title' | translate }}</mat-label>
            <mat-select
              formControlName="language"
              data-testid="profile-language-select"
              (selectionChange)="onLanguageChange($event.value)"
            >
              <mat-select-trigger class="flex items-center gap-1">
                @if (profileForm.get('language')?.value === 'de') {
                  <img src="/campaign-dashboard/flags/de.svg" class="w-4 h-4 inline-block" />
                  {{ 'pages.profile.language.german' | translate }}
                } @else if (profileForm.get('language')?.value === 'en') {
                  <img src="/campaign-dashboard/flags/uk.svg" class="w-4 h-4 inline-block" />
                  {{ 'pages.profile.language.english' | translate }}
                }
              </mat-select-trigger>

              <mat-option value="de" data-testid="profile-language-option-de">
                <div class="flex items-center gap-1">
                  <img src="/campaign-dashboard/flags/de.svg" class="w-4 h-4 inline-block" />
                  {{ 'pages.profile.language.german' | translate }}
                </div>
              </mat-option>
              <mat-option value="en">
                <div class="flex items-center gap-1">
                  <img src="/campaign-dashboard/flags/uk.svg" class="w-4 h-4 inline-block" />
                  {{ 'pages.profile.language.english' | translate }}
                </div>
              </mat-option>
            </mat-select>
          </mat-form-field>
        </form>

        <div class="flex gap-6 w-full justify-between">
          <button
            mat-flat-button
            color="secondary"
            type="button"
            [disabled]="store.loading$()"
            (click)="openChangePasswordDialog()"
          >
            {{ 'pages.profile.buttons.changePassword' | translate }}
          </button>

          <div class="flex gap-2">
            <button mat-flat-button color="danger" type="button" [disabled]="store.loading$()" (click)="signOut()">
              {{ 'pages.profile.banner.signOut' | translate }}
            </button>
            <button
              *hasPermission="[EPermissions.userUpdateOwn]"
              mat-flat-button
              type="submit"
              data-testid="profile-save-button"
              [disabled]="profileForm.invalid || store.loading$()"
              (click)="saveUserProfile()"
            >
              @if (store.loading$()) {
                <app-dots-spinner />
              } @else {
                {{ 'pages.profile.buttons.saveUser' | translate }}
              }
            </button>
          </div>
        </div>
      </section>
    </div>

    <div class="flex flex-col gap-6 mt-16 mb-20">
      <div class="flex items-center w-full p-4 bg-shikenso-gray-base rounded-2xl">
        <section class="flex flex-col gap-4 w-full">
          <!-- Section Title -->
          <div class="flex flex-col gap-1">
            <h3 class="text-2xl font-medium">{{ 'pages.profile.linkedAccounts.title' | translate }}</h3>
            <p class="text-sm text-shikenso-purple-base-60">
              {{ 'pages.profile.linkedAccounts.description' | translate }}
            </p>
          </div>

          <div class="flex flex-col gap-4">
            <!-- Google -->
            <div class="flex items-center justify-between p-2 border border-shikenso-purple-base-border rounded-full">
              <div class="flex items-center gap-2">
                <app-icon-badge
                  content="icons/login/google.svg"
                  backgroundColor="var(--shikenso-base-white)"
                  [ignoreIconColor]="true"
                  [size]="48"
                  [iconSize]="36"
                  [borderRadius]="100"
                />
                <div class="flex flex-col">
                  <span class="font-medium">Google</span>

                  @if (isProviderLinked('google.com')) {
                    <span class="text-sm text-shikenso-purple-base-60">{{ getProviderEmail('google.com') }}</span>
                  }
                </div>
              </div>

              @if (isProviderLinked('google.com')) {
                <button
                  *hasPermission="[EPermissions.userUpdateOwn]"
                  mat-flat-button
                  color="secondary"
                  [disabled]="store.loading$()"
                  (click)="unlinkProvider('google.com')"
                >
                  {{ 'pages.profile.linkedAccounts.unlink' | translate }}
                </button>
              } @else {
                <button
                  *hasPermission="[EPermissions.userUpdateOwn]"
                  mat-flat-button
                  color="secondary"
                  [disabled]="store.loading$()"
                  (click)="linkProvider('google')"
                >
                  {{ 'pages.profile.linkedAccounts.link' | translate }}
                </button>
              }
            </div>

            <!-- NOTE: Facebook and Twitter login require developer accounts -->
            <!-- Facebook -->
            <!-- <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div class="flex items-center gap-3">
            <app-icon-badge
              content="icons/login/facebook.svg"
              [size]="32"
              [iconSize]="16"
              [backgroundColor]="'#fff'"
              [borderRadius]="8"
              [borderWidth]="1"
            />
            <div class="flex flex-col">
              <span class="font-medium">Facebook</span>
              @if (isProviderLinked('facebook.com')) {
                <span class="text-sm text-gray-500">{{ getProviderEmail('facebook.com') }}</span>
              }
            </div>
          </div>
          @if (isProviderLinked('facebook.com')) {
            <button mat-stroked-button (click)="unlinkProvider('facebook.com')">
              {{ 'pages.profile.linkedAccounts.unlink' | translate }}
            </button>
          } @else {
            <button mat-stroked-button (click)="linkProvider('facebook')">
              {{ 'pages.profile.linkedAccounts.link' | translate }}
            </button>
          }
        </div> -->

            <!-- Twitter -->
            <!-- <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div class="flex items-center gap-3">
            <app-icon-badge
              content="icons/login/x.svg"
              [size]="32"
              [iconSize]="16"
              [backgroundColor]="'#fff'"
              [borderRadius]="8"
              [borderWidth]="1"
            />
            <div class="flex flex-col">
              <span class="font-medium">X (Twitter)</span>
              @if (isProviderLinked('twitter.com')) {
                <span class="text-sm text-gray-500">{{ getProviderEmail('twitter.com') }}</span>
              }
            </div>
          </div>
          @if (isProviderLinked('twitter.com')) {
            <button mat-stroked-button (click)="unlinkProvider('twitter.com')">
              {{ 'pages.profile.linkedAccounts.unlink' | translate }}
            </button>
          } @else {
            <button mat-stroked-button (click)="linkProvider('twitter')">
              {{ 'pages.profile.linkedAccounts.link' | translate }}
            </button>
          }
        </div> -->
          </div>
        </section>
      </div>

      @if (!(authService.currentDbUser$ | async)?.creatorId) {
        <div class="flex items-center w-full p-4 bg-shikenso-gray-base rounded-2xl" data-testid="profile-ai-assistants">
          <section class="flex flex-col gap-4 w-full">
            <div class="flex flex-col gap-1">
              <h3 class="text-2xl font-medium">{{ 'pages.profile.aiAssistants.title' | translate }}</h3>
              <p class="text-sm text-shikenso-purple-base-60">{{ aiAssistantsDescriptionKey | translate }}</p>
            </div>

            <div class="flex flex-col gap-4">
              @for (row of aiAssistantRows; track row.tab) {
                <div
                  class="flex items-center justify-between p-2 border border-shikenso-purple-base-border rounded-full"
                >
                  <div class="flex items-center gap-2">
                    <app-icon-badge
                      color="var(--shikenso-purple-base)"
                      [backgroundColor]="row.isBrandIcon ? 'var(--shikenso-base-white)' : 'var(--shikenso-pink-100-60)'"
                      [content]="row.icon"
                      [ignoreIconColor]="row.isBrandIcon"
                      [size]="48"
                      [iconSize]="row.iconSize"
                      [borderRadius]="100"
                    />
                    <div class="flex flex-col">
                      <span class="font-bold">{{ row.labelKey | translate }}</span>

                      @if (row.subtitleKey) {
                        <span class="text-sm text-shikenso-purple-base-60">{{ row.subtitleKey | translate }}</span>
                      }
                    </div>
                  </div>

                  <button
                    mat-flat-button
                    color="secondary"
                    type="button"
                    [attr.aria-label]="
                      'pages.profile.aiAssistants.connectAriaLabel' | translate: { app: row.labelKey | translate }
                    "
                    (click)="aiConnectorService.openDialog(EAiConnectorSource.profile, row.tab)"
                  >
                    {{ 'pages.profile.aiAssistants.connect' | translate }}
                  </button>
                </div>
              }
            </div>
          </section>
        </div>
      }
    </div>
  </app-content-container>
</div>
